import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
// Add static imports for main pages
import BookingPage from '@/pages/BookingPage.vue'
// Import the booking store
import { useBookingStore } from '@/stores/booking'

// Use the injected base path if available, otherwise fall back to Vite's base URL
// For test environment, ensure we use the /test/ base path
// Base path configuration handles multi-environment deployment routing
let base = (window as any).__BASE_PATH__ || import.meta.env.BASE_URL

// Define routes
const routes: Array<RouteRecordRaw> = [
  {
    path: '/booking',
    name: 'booking',
    // Use statically imported component instead of dynamic import
    component: BookingPage
  },
  // New routes for split member pages
  {
    path: '/member-confirmation/:bookingId',
    name: 'member-booking-confirmation',
    component: () => import('@/pages/MemberBookingConfirmationPage.vue'),
    props: true
  },
  {
    path: '/member-deposit/:bookingId',
    name: 'member-deposit-pending',
    component: () => import('@/pages/MemberDepositPendingPage.vue'),
    props: true
  },
  {
    path: '/pending/:bookingId',
    name: 'nonMemberPending',
    component: () => import('@/pages/NonMemberPendingPage.vue'),
    props: true
  },
  {
    path: '/booking-expired',
    name: 'bookingExpired',
    component: () => import('@/pages/BookingExpiredPage.vue')
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    redirect: { name: 'booking' }
  }
]

const router = createRouter({
  history: createWebHistory(base),
  routes,
  scrollBehavior() {
    // Always scroll to top
    return { top: 0 }
  }
})

// Universal query parameter handler
const handleQueryParameters = (to: any, from: any = {}) => {
  if (typeof window === 'undefined') return; // Skip if not in browser

  // Get all query parameters that changed
  const changedParams: Record<string, string> = {}

  // Identify which parameters have changed or are new
  Object.keys(to.query || {}).forEach(key => {
    const newValue = to.query[key]?.toString() || ''
    const oldValue = from?.query?.[key]?.toString() || ''

    if (newValue !== oldValue) {
      changedParams[key] = newValue
    }
  })

  const bookingStore = useBookingStore()

  // Handle specific parameters with their business logic
  if (Object.keys(changedParams).length > 0) {
    // Handle placeId parameter - updates store and clears cache when place changes
    if ('placeId' in changedParams) {
      // Only clear cache if placeId actually changed from a previous value
      const isNewPlaceId = bookingStore.placeId !== changedParams.placeId

      bookingStore.setPlaceId(changedParams.placeId)

      if (isNewPlaceId) {
        bookingStore.clearCache()
      }
    }

    // Handle countryCode parameter if needed
    // Currently we just pass it through without special handling
    if ('countryCode' in changedParams) {
      // Placeholder for future countryCode-specific logic (e.g., locale switching)
    }
  } else {
    // If no parameters changed but we're on a page that should have placeId
    // Check if we're on a route that should preserve placeId
    const routesToPreservePlaceId = ['nonMemberPending', 'member-booking-confirmation', 'member-deposit-pending']

    if (to.name && typeof to.name === 'string' &&
        routesToPreservePlaceId.includes(to.name) &&
        !to.query.placeId &&
        bookingStore.placeId !== 'default') {
      // Add the placeId to the URL without triggering a navigation
      // This ensures booking-related pages maintain place context
      if (to.query.countryCode) {
        to.query = {
          ...to.query,
          placeId: bookingStore.placeId
        }
      }
    }
  }
}

// Apply the handler on route changes
router.beforeEach((to, from) => {
  // Check if we need to preserve placeId in the URL for booking-related routes
  // This ensures users maintain their place context when navigating between booking pages
  const bookingStore = useBookingStore()
  const routesToPreservePlaceId = ['nonMemberPending', 'member-booking-confirmation', 'member-deposit-pending']

  if (to.name && typeof to.name === 'string' &&
      routesToPreservePlaceId.includes(to.name) &&
      !to.query.placeId &&
      bookingStore.placeId !== 'default') {
    // Create a new query object with the placeId to maintain booking context
    const newQuery = { ...to.query, placeId: bookingStore.placeId }

    // Return a redirect to the same route but with placeId in the query
    return {
      name: to.name,
      params: to.params,
      query: newQuery,
      hash: to.hash
    }
  }

  handleQueryParameters(to, from)
  return true
})

// Handle initial load - process query parameters when router is ready
// This ensures URL parameters are processed on direct page loads and refreshes
router.isReady().then(() => {
  const current = router.currentRoute.value
  handleQueryParameters(current)
})

export default router